/**
 * Effective workshop override — resolves default-vs-custom content mode.
 *
 * - `custom` → the client's override record (existing store)
 * - `default` → the shared program defaults (`useWorkshopContentDefaults`)
 *
 * Editors write through {@link setEffectiveOverride} so the active mode
 * decides the target store. Mode flips go through {@link setContentMode}.
 */

import type { Workshop } from "@/content/workshops";
import {
  resolveContentMode,
  type ContentMode,
  type WorkshopOverride,
  type WorkshopOverridePatch,
} from "@/features/workshop/overrides/workshop-override-content";
import {
  clearWorkshopOverrideField,
  clearWorkshopOverrides,
  getWorkshopOverride,
  setWorkshopOverride,
  useWorkshopOverride,
} from "@/stores/useWorkshopOverrides";
import {
  getWorkshopContentDefault,
  useWorkshopContentDefault,
  useWorkshopContentDefaults,
} from "@/stores/useWorkshopContentDefaults";

/** Reactive effective override for render sites + editors. */
export function useEffectiveWorkshopOverride(
  workshopId: string,
): WorkshopOverride | undefined {
  const client = useWorkshopOverride(workshopId);
  const defaults = useWorkshopContentDefault(workshopId);
  const mode = resolveContentMode(client);
  return mode === "custom" ? client : defaults;
}

/** Imperative read of the effective override. */
export function getEffectiveWorkshopOverride(
  workshopId: string,
): WorkshopOverride | undefined {
  const client = getWorkshopOverride(workshopId);
  const mode = resolveContentMode(client);
  if (mode === "custom") return client;
  return getWorkshopContentDefault(workshopId);
}

export function useWorkshopContentMode(workshopId: string): ContentMode {
  const client = useWorkshopOverride(workshopId);
  return resolveContentMode(client);
}

/** Write a field patch to whichever store the mode selects. */
export function setEffectiveOverride(
  workshopId: Workshop["id"],
  patch: WorkshopOverridePatch,
): void {
  const mode = resolveContentMode(getWorkshopOverride(workshopId));
  if (mode === "custom") {
    setWorkshopOverride(workshopId, { ...patch, contentMode: "custom" });
    return;
  }
  useWorkshopContentDefaults.getState().setDefault(workshopId, patch);
}

export function clearEffectiveOverrideField(
  workshopId: Workshop["id"],
  field: keyof WorkshopOverridePatch,
): void {
  const mode = resolveContentMode(getWorkshopOverride(workshopId));
  if (mode === "custom") {
    clearWorkshopOverrideField(workshopId, field);
    return;
  }
  useWorkshopContentDefaults.getState().clearDefaultField(workshopId, field);
}

export function clearEffectiveOverrides(workshopId: Workshop["id"]): void {
  const mode = resolveContentMode(getWorkshopOverride(workshopId));
  if (mode === "custom") {
    // Keep contentMode:custom but wipe fields — re-seed as empty custom.
    clearWorkshopOverrides(workshopId);
    setWorkshopOverride(workshopId, { contentMode: "custom" });
    return;
  }
  useWorkshopContentDefaults.getState().clearDefaults(workshopId);
}

/**
 * Flip default ↔ custom for this client.
 * - → custom: copy current effective content into the client record.
 * - → default: mark mode default (dormant custom fields kept for switch-back).
 */
export function setContentMode(
  workshopId: Workshop["id"],
  mode: ContentMode,
): void {
  if (mode === "default") {
    setWorkshopOverride(workshopId, { contentMode: "default" });
    return;
  }
  const effective = getEffectiveWorkshopOverride(workshopId);
  const {
    id: _id,
    workshopId: _ws,
    contentMode: _mode,
    ...fields
  } = effective ?? { id: "overrides", workshopId };
  setWorkshopOverride(workshopId, {
    ...fields,
    contentMode: "custom",
    updatedAt: Date.now(),
  });
}
